import moment from 'moment'
import retry from 'p-retry'
import queryString from 'query-string'
import store from 'store'

import agent from 'agent'
import RBACFallback from 'fixtures/v1/roles/GET_roles.json'
import log from 'log'
import {
  type CompanyEntitlementResponse,
  LimitTrackingPeriod
} from 'types/api/companies/entitlements'
import {
  type RbacBody,
  type RbacPrivilege,
  type RbacRolesAll,
  type RbacRoleTeam
} from 'types/api/rbac/rbac'
import { getBaseUrl } from 'utils/baseUrls'
import {
  flattenEntitlements,
  getEntitlementsExpirationProperties
} from 'utils/company-entitlements'
import { type ApiProp } from 'utils/endpoints-access'
import { isPlainObject } from 'utils/obj'
import openApiJsonStaging from 'utils/openapi-staging.json'
import openApiJson from 'utils/openapi.json'
import { formatTimePeriodDate } from 'utils/time/date'
import { ms } from 'utils/time/millisecond'

import type React from 'react'
import type { OpenApiResponse } from 'types/api/openapi'
import type { CompanyPreferences } from 'types/api/rbac/company'
import type {
  ChooseCompanyAuthResponse,
  MFAAuthResponse,
  PwdAuthResponse
} from 'types/api/user/session'
import type {
  UserSession,
  UserSessionTeam
} from 'types/api/users/sessions/user-session'

const OPENAPI_ENDPOINT = '/openapi.json'
export const OPENAPI_BASE_URL = `${getBaseUrl('api')}/v1${OPENAPI_ENDPOINT}`

type Limits = 'clients' | 'services' | 'targets' | 'teams' | 'users'

interface AuthContextEntitlementValues {
  contractExpirationImminent: boolean
  daysUntilContractExpiration: number
  hasAgentBilling: boolean
  hasAI: boolean
  hasReliabilityManagement: boolean
  hasFaultInjection: boolean
  hasFailureFlags: boolean
  isInternal: boolean
  isStrict: boolean
  isTrial: boolean
  limits: {
    [key in Limits]: number
  }
}

export interface AuthContextValues extends AuthContextEntitlementValues {
  RBAC: RbacBody
  authHeader: string
  companyDisplayName: string
  companyId: string
  companyIsAlfiEnabled: boolean // todo: EN-5114 deprecate
  companyPrefs: CompanyPreferences
  companyPrivileges: RbacPrivilege[]
  currentTeam: UserSessionTeam
  currentTeamId: string
  currentTeamRoles: RbacRoleTeam[]
  endSession: () => Promise<void>
  fetchSession: () => Promise<any>
  getTeamDetails: (
    teamId: string
  ) => UserSessionTeam | typeof TEAM_DETAILS_FALLBACK
  hasEndpointAccess: (
    apiProperties: ApiProp,
    options?: {
      teamId?: string
    }
  ) => boolean
  hasEndpointAccessForAll(
    apiProperties: ApiProp[],
    useTeamId?: boolean
  ): boolean
  isProductionEnvironment: boolean
  isRbacEnabled: boolean
  privilegesForTeam: (teamId: string) => RbacPrivilege[]
  ready: boolean
  renewSession: () => Promise<any>
  roles: RbacRolesAll[]
  session: UserSession
  startSession(activeSession?: UserSession, activeTeamId?: string): Promise<any>
  teams: UserSessionTeam[]
  setActiveTeamId: (teamId: string) => Promise<void>
  setAllAuth: React.Dispatch<
    React.SetStateAction<{
      ready: boolean
      session: UserSession
      currentTeamId: string
      isRbacEnabled: boolean
      RBAC: RbacBody
    }>
  >
}

export const TEAM_DETAILS_FALLBACK = {
  header: '',
  privileges: [] as RbacPrivilege[],
  roles: [] as RbacRolesAll[]
}

export const BASE_COMPANY_ENTITLEMENTS_RESPONSE: CompanyEntitlementResponse = {
  company_id: '',
  created_at: formatTimePeriodDate(moment().subtract(32, 'days')),
  end: formatTimePeriodDate(moment().add(8, 'days')),
  internal: false,
  strict: true,
  has_agent_billing: true,
  has_trial_banners: false,
  product_flags: {
    hasProductAI: false,
    hasProductFI: false,
    hasProductRM: false,
    hasProductFF: false
  },
  limits: {
    limitTargets: 0,
    limitUsers: 0,
    limitServices: 0,
    limitAgents: 0,
    limitTeams: 0
  },
  targets_tracking_period: LimitTrackingPeriod.Monthly
}

export const BASE_SESSION: UserSession = {
  company_id: '',
  company_name: '',
  company_alias: '',
  company_is_alfi_enabled: false,
  company_entitlements_response: BASE_COMPANY_ENTITLEMENTS_RESPONSE,
  roles: [],
  privileges: [],
  expires_at: '',
  header: '',
  identifier: '',
  first_name: '',
  last_name: '',
  teams: [],
  last_login: '',
  renew_token: '',
  token: '',
  prefs: {} as CompanyPreferences
}

export const DEFAULT_AUTH_VALUES: AuthContextValues = {
  RBAC: RBACFallback as unknown as RbacBody /** The RBAC roles and privileges summary */,
  authHeader:
    BASE_SESSION.header /** The authorization header used for requests */,
  companyDisplayName:
    BASE_SESSION.company_name /** Preferred display name of the company */,
  companyId: BASE_SESSION.company_id,
  companyIsAlfiEnabled: BASE_SESSION.company_is_alfi_enabled,
  companyPrefs: BASE_SESSION.prefs,
  companyPrivileges: [],
  contractExpirationImminent: false,
  currentTeam: {} as UserSessionTeam,
  currentTeamId: '',
  currentTeamRoles: [],
  daysUntilContractExpiration: 8,
  endSession: async () => {},
  fetchSession: async () => {},
  getTeamDetails: () => TEAM_DETAILS_FALLBACK,
  hasAgentBilling: BASE_COMPANY_ENTITLEMENTS_RESPONSE.has_agent_billing,
  hasAI: BASE_COMPANY_ENTITLEMENTS_RESPONSE.product_flags.hasProductAI,
  hasEndpointAccess: () => false,
  hasEndpointAccessForAll: () => false,
  hasReliabilityManagement:
    BASE_COMPANY_ENTITLEMENTS_RESPONSE.product_flags.hasProductRM,
  hasFaultInjection:
    BASE_COMPANY_ENTITLEMENTS_RESPONSE.product_flags.hasProductFI,
  hasFailureFlags:
    BASE_COMPANY_ENTITLEMENTS_RESPONSE.product_flags.hasProductFF,
  isProductionEnvironment: false,
  isRbacEnabled: false,
  isInternal: BASE_COMPANY_ENTITLEMENTS_RESPONSE.internal,
  isStrict: BASE_COMPANY_ENTITLEMENTS_RESPONSE.strict,
  isTrial: BASE_COMPANY_ENTITLEMENTS_RESPONSE.has_trial_banners,
  limits: {
    clients: BASE_COMPANY_ENTITLEMENTS_RESPONSE.limits.limitAgents,
    services: BASE_COMPANY_ENTITLEMENTS_RESPONSE.limits.limitServices,
    targets: BASE_COMPANY_ENTITLEMENTS_RESPONSE.limits.limitTargets,
    teams: BASE_COMPANY_ENTITLEMENTS_RESPONSE.limits.limitTeams,
    users: BASE_COMPANY_ENTITLEMENTS_RESPONSE.limits.limitUsers
  },
  privilegesForTeam: () => [],
  ready: false,
  renewSession: async () => {},
  roles: BASE_SESSION.roles,
  session: BASE_SESSION,
  startSession: async () => {},
  setActiveTeamId: async () => {},
  setAllAuth: () => {},
  teams: []
}

export function getRolesFromLocalStorage() {
  const storedSummary = store.get('rbac-summary')
  const { date, summary } = storedSummary ?? {}

  if (
    date &&
    summary &&
    Number(new Date().getTime() - new Date(date).getTime()) <= ms.days(1)
  )
    return summary as RbacBody
}

export async function getUserSession(session: UserSession) {
  const res = await agent.get<UserSession>(
    '/users/sessions?getCompanySession=true',
    {
      headers: {
        Authorization: session.header
      }
    }
  )
  return res.data
}

export async function renewUserSession(session: UserSession) {
  // todo: determine type of response
  const res = await agent.post(
    '/users/renew/rbac',
    queryString.stringify({
      email: session.identifier,
      companyId: session.company_id,
      renewToken: session.renew_token
    }),
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }
  )
  return res.data
}

export function setActiveTeamIdInLocalStorage(
  teamId: string,
  companyId: string
) {
  const activeTeamIdValue = store.get('lastActiveOrgId')
  if (isPlainObject(activeTeamIdValue)) {
    return store.set('lastActiveOrgId', {
      ...activeTeamIdValue,
      [companyId]: teamId
    })
  } else {
    store.set('lastActiveOrgId', {
      [companyId]: teamId
    })
  }
}

export function getActiveTeamIdFromLocalStorage(companyId: string) {
  const activeTeamIdValue: string | { [k: string]: string } =
    store.get('lastActiveOrgId')

  if (typeof activeTeamIdValue === 'string') {
    return activeTeamIdValue
  } else if (isPlainObject(activeTeamIdValue)) {
    return activeTeamIdValue[companyId]
  }
}

export function getOpenApi({
  onSuccess
}: {
  onSuccess: (openApi: OpenApiResponse) => void
}) {
  // do not await here. It's an async callback because it can take a long time to load
  retry(
    async () => {
      const { data } = await agent.get<OpenApiResponse>(OPENAPI_ENDPOINT)
      onSuccess(data)
    },
    {
      maxTimeout: ms.minutes(5),
      minTimeout: ms.seconds(10),
      onFailedAttempt: log.error
    }
  )

  return openApiJson as unknown as OpenApiResponse
}

/**
 * Check both the received OpenApiResponse and fallback OpenApiResponse
 * for the privilege associated with the given endpoint and method
 */
export function getPrivilegeForEndpoint(
  endpoint: string,
  method: string,
  openApi?: OpenApiResponse
) {
  const openApiPaths = openApi?.paths
  const fallbackPaths = (openApiJson as unknown as OpenApiResponse).paths
  const fallbackPathsStaging = (
    openApiJsonStaging as unknown as OpenApiResponse
  ).paths

  try {
    // @ts-expect-error the path may not exist and throwing error is expected
    return openApiPaths[endpoint][method].security[0].privilege[0]
  } catch {
    // pass
  }
  try {
    // @ts-expect-error the path may not exist and throwing error is expected
    return fallbackPaths[endpoint][method].security[0].privilege[0]
  } catch {
    // pass
  }
  try {
    /**
     * This is mainly for endpoints that are behind feature flags
     * and tests need to access them.
     * Ideally, we shouldn't reach this during runtime as feature flag
     * checks should guard/prevent access checks.
     */

    // @ts-expect-error the path may not exist and throwing error is expected
    return fallbackPathsStaging[endpoint][method].security[0].privilege[0]
  } catch (e) {
    log.error(`Warning: issue checking access for endpoint:
    endpoint: ${endpoint}
    method: ${method}
    openApiPaths: ${
      openApiPaths
        ? // send 200 chars so that we can get the general idea, but not send too much data to sentry
          `${JSON.stringify(openApiPaths).slice(0, 200)}...`
        : openApiPaths
    }
    error: ${e.message}`)
    return null
  }
}

export function getEntitlementsForAuth(
  session: UserSession
): AuthContextEntitlementValues {
  const entitlements = flattenEntitlements(
    // todo: EN-6511 remove company_entitlements_response after migration
    // this makes it backwards-compatible so deploy doesn't have to be coordinated
    session?.company_entitlements_response ??
      session?.entitlements ??
      BASE_COMPANY_ENTITLEMENTS_RESPONSE
  )

  const { contractExpirationImminent, daysUntilContractExpiration } =
    getEntitlementsExpirationProperties(entitlements.dateEnd)

  return {
    contractExpirationImminent,
    daysUntilContractExpiration,
    hasAgentBilling: entitlements.hasAgentBilling,
    hasAI: entitlements.hasProductAI,
    hasFaultInjection: entitlements.hasProductFI,
    hasReliabilityManagement: entitlements.hasProductRM,
    hasFailureFlags: entitlements.hasProductFF,
    isInternal: entitlements.isInternal,
    isStrict: entitlements.isStrict,
    isTrial: entitlements.hasTrialBanners,
    limits: {
      clients: entitlements.limitAgents,
      services: entitlements.limitServices,
      targets: entitlements.limitTargets,
      teams: entitlements.limitTeams,
      users: entitlements.limitUsers
    }
  }
}

export function isChooseCompanyAuthResponse(
  response: PwdAuthResponse
): response is ChooseCompanyAuthResponse {
  return (
    typeof response !== 'string' &&
    'result' in response &&
    response.result === 'chooseCompany'
  )
}

export function isMFAAuthResponse(
  response: PwdAuthResponse
): response is MFAAuthResponse {
  return (
    typeof response !== 'string' &&
    'qrUrl' in response &&
    'secretKey' in response
  )
}
